import { Controller } from 'stimulus';

const SETTLE_MS = 400;
const BUSY_RETRY_MS = 1500;

// The Create Job tasks table (step 3, jobs/tasks/_tasks_table). Re-fetches
// itself (create_jobs#reload_tasks_table) when the job's samples change, so its
// chips and counts stay true: after a request that changes the samples table
// (add, copy, edit, remove, add existing) finishes, or the samples table
// reloads over the cable. Reads (opening a cell editor, searching) don't
// count. Open chips rows stay open, and the swap waits while someone is
// typing in the table or has a menu open. Marks itself is-empty while it has
// no tasks, which shows the empty row and hides Assign Samples. A task's
// sample count opens its chips row.
export default class extends Controller {
  connect() {
    this.onBeforeSend = this.onBeforeSend.bind(this);
    this.schedule = this.schedule.bind(this);
    // ajax:beforeSend fires while the link or form is still on the page; the
    // completion events can fire on a row the response has already removed
    // (Delete), so they never reach the document. The xhr outlives the row.
    document.addEventListener('ajax:beforeSend', this.onBeforeSend);
    document.addEventListener('resourceable:reloaded', this.schedule);

    this.observer = new MutationObserver(() => this.sync());
    this.observer.observe(this.element, { childList: true, subtree: true });
    this.sync();
  }

  disconnect() {
    document.removeEventListener('ajax:beforeSend', this.onBeforeSend);
    document.removeEventListener('resourceable:reloaded', this.schedule);
    this.observer.disconnect();
    clearTimeout(this.timer);
  }

  onBeforeSend(event) {
    const [xhr, options] = event.detail || [];
    if (!xhr || !options) return;
    const method = (options.type || 'GET').toUpperCase();
    if (method === 'GET' || !/\/v2_samples([/?]|$)/.test(options.url || '')) return;
    xhr.addEventListener('loadend', this.schedule);
  }

  schedule() {
    clearTimeout(this.timer);
    this.timer = setTimeout(() => this.load(), SETTLE_MS);
  }

  // Typing in a Seq box, or a ⋯ menu open: try again shortly.
  busy() {
    const active = document.activeElement;
    if (active && this.element.contains(active) && /^(INPUT|SELECT|TEXTAREA)$/.test(active.tagName)) return true;
    return !!this.element.querySelector('.ct-menu:not(.hidden)');
  }

  load() {
    if (this.busy()) {
      this.timer = setTimeout(() => this.load(), BUSY_RETRY_MS);
      return;
    }
    fetch(this.data.get('url'), {
      credentials: 'same-origin',
      headers: { Accept: 'text/html', 'X-Requested-With': 'XMLHttpRequest' }
    })
      .then(response => (response.ok ? response.text() : Promise.reject(response)))
      .then((html) => {
        const open = Array.from(this.element.querySelectorAll('tbody.ct-task.is-open')).map(tbody => tbody.id);
        this.element.innerHTML = html;
        open.forEach((id) => {
          const tbody = document.getElementById(id);
          if (!tbody || !this.element.contains(tbody)) return;
          tbody.classList.add('is-open');
          const button = tbody.querySelector('.ct-count');
          if (button) button.setAttribute('aria-expanded', 'true');
        });
      })
      .catch(() => {});
  }

  toggle(event) {
    const button = event.currentTarget;
    const open = button.closest('tbody').classList.toggle('is-open');
    button.setAttribute('aria-expanded', open);
  }

  sync() {
    this.element.classList.toggle('is-empty', !this.element.querySelector('tbody.ct-task'));
  }
}
